import { TreeAction } from '@daymade/common-lib/dbEnums';
import { ZEntryLine } from '@daymade/common-lib/dbJsonModels';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  ZDrawScheduleSelect,
  ZDrawSelect,
  ZDrawTypePlanSelect,
} from '../draw/draw';
import { ZDrawOrderSelect } from '../draw/drawOrder/models';
import { ZPaymentCurrency, ZPaymentMethod } from '../payment';
import { ZSubscriptionSelect } from '../subscription/models';
import {
  ZCreateOrderResponse,
  ZOrderSelect,
  ZPurchasingAction,
} from './models';

const c = initContract();
export const authOrderContract = c.router(
  {
    getOrderStatus: {
      method: 'GET',
      path: '/status/:order_id',
      responses: {
        200: z.object({
          status: z.string(),
        }),
        404: c.type<{ message: string }>(),
      },
    },
    getOrderDetailsByAdyenPaymentSession: {
      method: 'GET',
      path: '/details/adyen/:adyen_payment_session_id',
      responses: {
        200: z.object({
          order: ZOrderSelect,
          drawOrder: ZDrawOrderSelect.nullable(),
          firstDraw: ZDrawSelect.nullable(),
          drawSchedulePlans: z
            .object({
              drawSchedule: ZDrawScheduleSelect,
              drawTypePlan: ZDrawTypePlanSelect,
            })
            .array()
            .nullable(),
          subscription: ZSubscriptionSelect.nullable(),
          promoCode: z.string().nullable(),
        }),
        404: c.type<{ message: string }>(),
      },
    },

    getOrderDetails: {
      method: 'GET',
      path: '/details/:order_id',
      responses: {
        200: z.object({
          order: ZOrderSelect,
          drawOrder: ZDrawOrderSelect.nullable(),
          firstDraw: ZDrawSelect.nullable(),
          drawSchedulePlans: z
            .object({
              drawSchedule: ZDrawScheduleSelect,
              drawTypePlan: ZDrawTypePlanSelect,
            })
            .array()
            .nullable(),
          subscription: ZSubscriptionSelect.nullable(),
          promoCode: z.string().nullable(),
        }),
        404: c.type<{ message: string }>(),
      },
    },

    createOrder: {
      method: 'POST',
      path: '/create',
      responses: {
        200: ZCreateOrderResponse,
        400: z.string(),
      },
      body: z.object({
        purchasingAction: ZPurchasingAction,
        drawPurchase: z
          .object({
            entries: z.array(ZEntryLine),
            drawId: z.number().optional(),
            drawTypeCode: z.string(),
            drawSchedulePlans: z
              .object({
                drawScheduleId: z.coerce.number(),
                drawTypePlanCode: z.string(),
                drawTypePlanSubcode: z.string(),
              })
              .array(),
          })
          .optional(),
        prizePurchase: z
          .object({
            prizeTierId: z.number(),
            prizeId: z.number(),
            productId: z.number().optional(),
            deliveryStreet: z.string().optional(),
            deliveryCity: z.string().optional(),
            deliveryPostcode: z.string().optional(),
            emailAddress: z.string().optional(),
            phoneNumber: z.string().optional(),
            additionalInfoFromPlayer: z
              .record(z.string(), z.string())
              .optional(),
          })
          .optional(),
        treePurchase: z
          .object({
            action: z.nativeEnum(TreeAction),
            quantity: z.number(),
          })
          .optional(),
        tokenPurchase: z
          .object({
            tokenAmount: z.number(),
          })
          .optional(),
        paymentCurrency: ZPaymentCurrency.optional(),
        expectedCost: z.number().optional(),
        paymentMethod: ZPaymentMethod.optional(),
        sourceFor3DS: z.string().optional(),
        promoCode: z.string().optional(),
        referralId: z.string().optional(),
        utmParams: z.record(z.string(), z.string()).nullish(),
        attributionParams: z.record(z.string(), z.string()).nullish(),
      }),
    },
  },
  {
    pathPrefix: '/auth',
    strictStatusCodes: true,
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
  },
);
